import { queryOptions, useQuery } from '@tanstack/react-query';

import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOADS_RELIABILITY_SUMMARIES_QUERY_KEY =
  'workloads-reliability-summaries';

type UseWorkloadReliabilitySummariesQueryArgs = {
  clusterId?: string;
  startTime?: string;
  endTime?: string;
  timezone?: string;
  workloadNames?: string[];
  workloadTypes?: string[];
  namespaces?: string[];
  enabled?: boolean;
};

const getWorkloadReliabilitySummariesQueryKey = ({
  clusterId,
  startTime,
  endTime,
  timezone,
  workloadNames,
  workloadTypes,
  namespaces,
}: Pick<
  UseWorkloadReliabilitySummariesQueryArgs,
  | 'clusterId'
  | 'startTime'
  | 'endTime'
  | 'timezone'
  | 'workloadNames'
  | 'workloadTypes'
  | 'namespaces'
>) =>
  [
    WORKLOADS_RELIABILITY_SUMMARIES_QUERY_KEY,
    clusterId,
    startTime,
    endTime,
    timezone,
    workloadNames,
    workloadTypes,
    namespaces,
  ] as const;

export const getWorkloadReliabilitySummariesQueryOptions = ({
  clusterId,
  startTime,
  endTime,
  timezone,
  workloadNames,
  workloadTypes,
  namespaces,
  enabled = true,
}: UseWorkloadReliabilitySummariesQueryArgs) => {
  const isFiltersReady = !!clusterId && !!startTime && !!endTime && !!timezone;

  return queryOptions({
    queryKey: getWorkloadReliabilitySummariesQueryKey({
      clusterId,
      startTime,
      endTime,
      timezone,
      workloadNames,
      workloadTypes,
      namespaces,
    }),
    queryFn: async () => {
      const { data } =
        await apiClient.costReport.getWorkloadReliabilitySummaries({
          clusterId: clusterId!,
          startTime: startTime!,
          endTime: endTime!,
          timezone: timezone!,
          workloadNames,
          workloadTypes,
          namespaces,
        });

      return data;
    },
    enabled: makeIsEnabled(enabled, isFiltersReady),
  });
};

export const useWorkloadReliabilitySummariesQuery = (
  props: UseWorkloadReliabilitySummariesQueryArgs
) => useQuery(getWorkloadReliabilitySummariesQueryOptions(props));
